import { INTRO_COMMISSION_ID, type CharacterCommission, type Commission } from '@sorrem/shared';
import { useCallback } from 'react';
import { useAcceptCommission } from './use-accept-commission';
import { useCharacterCommissions } from './use-character-commissions';
import { useCommission } from './use-commission';

export interface IntroCommissionState {
    /** The selected character's intro commission, or null if it has none (or it hasn't loaded). */
    commission: CharacterCommission | null;
    /** The intro commission's content, or null until it has loaded. */
    content: Commission | null;
    isLoading: boolean;
    isAccepting: boolean;
    /**
     * Accepts the commission, creating the Crucible it points to. Resolves to
     * the new crucible's id, or null if accepting failed.
     */
    accept: () => Promise<string | null>;
}

/** The selected character's intro commission (see docs/gameplay/game-intro.md). */
export function useIntroCommission(): IntroCommissionState {
    const { commissions, isLoading } = useCharacterCommissions();
    const commission = commissions.find((entry) => entry.commissionId === INTRO_COMMISSION_ID) ?? null;
    const { commission: content, isLoading: isContentLoading } = useCommission(commission !== null ? INTRO_COMMISSION_ID : null);
    const { isAccepting, accept: acceptCommission } = useAcceptCommission();

    const accept = useCallback((): Promise<string | null> => acceptCommission(INTRO_COMMISSION_ID), [acceptCommission]);

    return { commission, content, isLoading: isLoading || isContentLoading, isAccepting, accept };
}
